import React, { useEffect, useState } from "react";
import { Star } from "lucide-react";
import { Review } from "../../../types/Response/CustomerResponse";


interface ReviewSectionProps {
  reviews?: Review[];
}

const ReviewSection: React.FC<ReviewSectionProps> = ({ reviews = [] }) => {
  const [visible, setVisible] = useState(3);
  const [current, setCurrent] = useState(0);

  const total = reviews.length;
  const maxIndex = Math.max(0, total - visible);

  // Handle responsiveness for visible items count
  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth < 640) {
        setVisible(1);
      } else if (window.innerWidth < 1024) {
        setVisible(2);
      } else {
        setVisible(3);
      }
    };

    handleResize();
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  // Auto-slide only if there are enough reviews to scroll
  useEffect(() => {
    if (maxIndex <= 0) return;

    const interval = setInterval(() => {
      setCurrent((prev) => (prev >= maxIndex ? 0 : prev + 1));
    }, 4000);

    return () => clearInterval(interval);
  }, [maxIndex]);

  useEffect(() => {
    if (current > maxIndex) {
      setCurrent(maxIndex);
    }
  }, [maxIndex, current]);

  if (!reviews || reviews.length === 0) {
    return null;
  }

  return (
    <div className="bg-white rounded-2xl">
      {/* HEADER */}
      <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-3 pb-2">
        <div>
          <h2 className="text-xl md:text-2xl lg:text-2xl font-bold text-iconColor">
            Customer Reviews
          </h2>
          <p className="text-sm text-slate-500">
            Real feedback from verified pet parents
          </p>
        </div>
      </div>

      {/* SLIDER / GRID */}
      <div className="overflow-hidden w-full px-1 pt-4">
        <div
          className="flex transition-transform duration-500 ease-in-out"
          style={{
            transform: `translateX(calc(-${current * 100}% / ${visible}))`,
          }}
        >
          {reviews.map((rev, i) => {
            const isCenter = visible === 3 ? i === current + 1 : false;

            return (
              <div
                key={i}
                className="flex-[0_0_100%] md:flex-[0_0_50%] lg:flex-[0_0_33.333%] px-3"
              >
                <div
                  className={`h-full flex flex-col justify-between py-5 px-4 rounded-[2rem] transition-all duration-300 ${
                    isCenter
                      ? "bg-logoHeading text-white scale-105 shadow-xl"
                      : "bg-white text-logoHeading border border-slate-200"
                  }`}
                >
                  <div className="space-y-3">
                    {/* Star Rating */}
                    <div className="flex items-center gap-1">
                      {Array.from({ length: 5 }).map((_, index) => (
                        <Star
                          key={index}
                          size={14}
                          className={`${
                            index < rev.rating
                              ? "fill-yellow-400 text-yellow-400"
                              : isCenter
                              ? "text-white/30"
                              : "text-slate-200"
                          }`}
                        />
                      ))}
                    </div>

                    {/* Review Text */}
                    <p className="text-sm italic leading-relaxed">
                      "{rev.comment || "Great product!"}"
                    </p>

                    {/* Review Image Attachments */}
                    {rev.reviewImages && rev.reviewImages.length > 0 && (
                      <div className="flex items-center gap-2 pt-1 flex-wrap">
                        {rev.reviewImages.map((imgUrl, imgIdx) => (
                          <img
                            key={imgIdx}
                            src={imgUrl}
                            alt={`Review image ${imgIdx + 1}`}
                            className="w-12 h-12 rounded-lg object-cover border border-white/20 shadow-xs"
                          />
                        ))}
                      </div>
                    )}
                  </div>

                  {/* Customer Info */}
                  <div className="flex items-center gap-3 pt-4 mt-3 border-t border-slate-100/20">
                    <div className="w-10 h-10 rounded-full overflow-hidden shrink-0 border border-slate-200 bg-slate-100">
                      {rev.customerImage ? (
                        <img
                          src={rev.customerImage}
                          alt={rev.customerName || "Customer"}
                          className="w-full h-full object-cover"
                        />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center font-bold text-xs text-slate-500">
                          {rev.customerName?.charAt(0) || "U"}
                        </div>
                      )}
                    </div>
                    <div>
                      <p className="text-xs font-black">
                        {rev.customerName || "Verified Buyer"}
                      </p>
                      {rev.customerLocation && (
                        <p className="text-[10px] font-bold opacity-70">
                          {rev.customerLocation}
                        </p>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default ReviewSection;